import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowLeft, MapPin, Briefcase, Award, Image as ImageIcon, X, Building2, User, UserSearch, Linkedin, Mail, Globe, Lock, UserPlus, LogIn, Search, ShieldCheck, Loader2 } from "lucide-react";
import { SkillsBadge } from "@/components/SkillsUI";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SupportersPanel, SUP_TR } from "@/components/SupportersPanel";
import { HeartHandshake } from "lucide-react";
import { CandidateSearchPanel } from "@/components/CandidateSearchPanel";

const TR = {
  sk: {
    back: "Späť",
    eyebrow: "Sieň slávy",
    title: "SauberEko® Certifikovaní",
    subtitle: "Verejný register profesionálov a firiem, ktorí prešli prísnym overením SauberEko®. Každý certifikát značky SauberEko® je viazaný na unikátne evidenčné číslo.",
    tabPros: "Profesionáli",
    tabCompanies: "Firmy",
    tabRecognitions: "Uznania",
    tabQualified: "Preverení a absolventi",
    qualifiedIntro: "Preverení personalisti a absolventi programu „Work, learn and earn©“ — praxou preverení profesionáli.",
    emptyQualifiedTitle: "Zatiaľ žiadne záznamy",
    emptyQualifiedDesc: "Ako pribúdajú audity mentorov a overenia personalistov, objavia sa tu.",
    graduateBadge: "🎓 Absolvent",
    cardIdLabel: "Card ID",
    recognitionsIntro: "Ocenenia a vyznamenania udelené značkou SauberEko® — Osobnosti, Firmy, Životné dielo. Verejný register bez paywall-u.",
    emptyRecognitionsTitle: "Zatiaľ žiadne uznania",
    emptyRecognitionsDesc: "Ako značka SauberEko® udelí prvé vyznamenanie, objaví sa tu.",
    recognitionAwarded: "Udelené",
    recognitionCategory: "Kategória",
    emptyProsTitle: "Zatiaľ žiadni certifikovaní profesionáli",
    emptyProsDesc: "Sieň slávy sa plní postupne, ako pribúdajú držitelia certifikátu Sa.Eko.®",
    emptyCompTitle: "Zatiaľ žiadne certifikované firmy",
    emptyCompDesc: "Pribúdajú postupne, ako firmy získavajú certifikát SauberEko®.",
    verifyEyebrow: "Overenie certifikátu",
    verifyTitlePro: "Overiť profesionála",
    verifyTitleRec: "Overiť personalistu",
    verifyGdprHint: "Kvôli ochrane osobných údajov nezobrazujeme verejný zoznam certifikovaných jednotlivcov. Zadaj priezvisko alebo číslo certifikátu — ak osoba existuje, uvidíš verejnú vizitku.",
    verifyPlaceholder: "Priezvisko alebo číslo certifikátu…",
    verifyMinChars: "Zadaj aspoň 3 znaky · max 8 výsledkov",
    verifyEmptyPro: "Žiadny profesionál sa nezhoduje",
    verifyEmptyRec: "Žiadny personalista sa nezhoduje",
    verifyEmptyDesc: "Skús iný tvar mena alebo skontroluj správnosť čísla certifikátu.",
    verifiedBadge: "OVERENÝ",
    sectorIndustry: "Priemysel & Výroba",
    sectorServices: "Služby",
    sectorUnclassified: "Nezaradené",
    sectorBack: "Späť na výber",
    sectorPromptTitle: "Vyberte oblasť podnikania",
    sectorPromptDesc: "Certifikované firmy sme rozdelili podľa sektora, aby ste rýchlo našli tú správnu.",
    emptyRecTitle: "Zatiaľ žiadni certifikovaní personalisti",
    emptyRecDesc: "Pribúdajú postupne, ako personálni manažéri získavajú certifikát SauberEko®.",
    certNo: "Certifikát č.",
    certifiedSince: "Certifikovaný od",
    extraCerts: "ďalších certifikátov",
    viewProfile: "Profil",
    viewCert: "Pozri certifikát",
    visitWeb: "Web",
    closeCert: "Zavrieť",
    gateTitle: "Sieň slávy je dostupná len pre registrovaných členov",
    gateBody: "Verejný register SauberEko® certifikovaných profesionálov a firiem je viditeľný len pre prihlásených členov Tigiku. Registrácia je bezplatná a zaberie ti pár sekúnd.",
    gateOfficialLink: "Oficiálny register je dostupný aj na",
    gateRegister: "Zaregistrovať sa",
    gateLogin: "Prihlásiť sa",
    paywallTitle: "Sieň slávy je iba pre ACTIVE a vyšších členov",
    paywallBody: "Sieň slávy SauberEko® je výsadou platených členov Tigik. Aktivuj si ACTIVE (€3,99/týždeň) alebo PRO (€6,99/mesiac) a získaš prístup k celej databáze certifikovaných profesionálov, firiem aj personalistov.",
    paywallBtn: "Aktivovať predplatné",
    paywallSecondaryBtn: "Získať certifikát zdarma",
  },
  en: {
    back: "Back",
    eyebrow: "Hall of Fame",
    title: "SauberEko® Certified",
    subtitle: "Public registry of professionals and companies who passed the strict SauberEko® verification. Every SauberEko® certificate is tied to a unique registry number.",
    tabPros: "Professionals",
    tabCompanies: "Companies",
    tabRecognitions: "Recognitions",
    tabQualified: "Verified & Graduates",
    qualifiedIntro: "Verified HR / recruiters and \"Work, learn and earn©\" program graduates — professionals vetted by hands-on audit.",
    emptyQualifiedTitle: "No entries yet",
    emptyQualifiedDesc: "As mentor audits and HR certifications come in, they appear here.",
    graduateBadge: "🎓 Graduate",
    cardIdLabel: "Card ID",
    recognitionsIntro: "Honors and awards granted by the SauberEko® brand — personalities, companies, lifetime achievement. Public register, no paywall.",
    emptyRecognitionsTitle: "No recognitions yet",
    emptyRecognitionsDesc: "As soon as the SauberEko® brand grants its first honor, it will appear here.",
    recognitionAwarded: "Awarded",
    recognitionCategory: "Category",
    emptyProsTitle: "No certified professionals yet",
    emptyProsDesc: "The hall of fame fills as new Sa.Eko.® certificate holders are added.",
    emptyCompTitle: "No certified companies yet",
    emptyCompDesc: "Companies are added as they achieve SauberEko® certification.",
    verifyEyebrow: "Certificate verification",
    verifyTitlePro: "Verify a professional",
    verifyTitleRec: "Verify an HR / recruiter",
    verifyGdprHint: "To protect personal data we do not list certified individuals publicly. Enter a surname or certificate number — if the person exists you'll see their public verification card.",
    verifyPlaceholder: "Surname or certificate number…",
    verifyMinChars: "Type at least 3 characters · max 8 results",
    verifyEmptyPro: "No matching professional",
    verifyEmptyRec: "No matching recruiter",
    verifyEmptyDesc: "Try a different spelling or double-check the certificate number.",
    verifiedBadge: "VERIFIED",
    sectorIndustry: "Industry & Manufacturing",
    sectorServices: "Services",
    sectorUnclassified: "Unclassified",
    sectorBack: "Back to selection",
    sectorPromptTitle: "Choose a business sector",
    sectorPromptDesc: "We split certified companies by sector so you can quickly find the right one.",
    emptyRecTitle: "No certified recruiters yet",
    emptyRecDesc: "Recruiters & HR managers are added as they achieve SauberEko® certification.",
    certNo: "Certificate No.",
    certifiedSince: "Certified since",
    extraCerts: "more certificates",
    viewProfile: "Profile",
    viewCert: "View certificate",
    visitWeb: "Website",
    closeCert: "Close",
    gateTitle: "Hall of Fame is for registered members only",
    gateBody: "The public registry of SauberEko® certified professionals and companies is only visible to logged-in Tigik members. Registration is free and takes a few seconds.",
    gateOfficialLink: "The official registry is also available at",
    gateRegister: "Sign up",
    gateLogin: "Log in",
    paywallTitle: "Hall of Fame is for ACTIVE+ members",
    paywallBody: "SauberEko® Hall of Fame is reserved for paying Tigik members. Activate ACTIVE (€3.99/week) or PRO (€6.99/month) to access the full database of certified professionals, companies and recruiters.",
    paywallBtn: "Activate subscription",
    paywallSecondaryBtn: "Get certified (free)",
  },
  cs: {
    back: "Zpět",
    eyebrow: "Síň slávy",
    title: "SauberEko® Certifikovaní",
    subtitle: "Veřejný registr profesionálů a firem, kteří prošli přísným ověřením SauberEko®. Každý certifikát značky SauberEko® je vázán na unikátní evidenční číslo.",
    tabPros: "Profesionálové",
    tabCompanies: "Firmy",
    tabRecognitions: "Uznání",
    tabQualified: "Prověření a absolventi",
    qualifiedIntro: "Prověření personalisté a absolventi programu „Work, learn and earn©“ — praxí prověření profesionálové.",
    emptyQualifiedTitle: "Zatím žádné záznamy",
    emptyQualifiedDesc: "Jak přibývají audity mentorů a ověření personalistů, objeví se zde.",
    graduateBadge: "🎓 Absolvent",
    cardIdLabel: "Card ID",
    recognitionsIntro: "Vyznamenání a ocenění udělená značkou SauberEko® — osobnosti, firmy, celoživotní dílo. Veřejný registr bez paywall-u.",
    emptyRecognitionsTitle: "Zatím žádná uznání",
    emptyRecognitionsDesc: "Jakmile značka SauberEko® udělí první vyznamenání, objeví se zde.",
    recognitionAwarded: "Uděleno",
    recognitionCategory: "Kategorie",
    emptyProsTitle: "Zatím žádní certifikovaní profesionálové",
    emptyProsDesc: "Síň slávy se plní postupně, jak přibývají držitelé certifikátu Sa.Eko.®",
    emptyCompTitle: "Zatím žádné certifikované firmy",
    emptyCompDesc: "Firmy přibývají postupně, jak získávají certifikát SauberEko®.",
    verifyEyebrow: "Ověření certifikátu",
    verifyTitlePro: "Ověřit profesionála",
    verifyTitleRec: "Ověřit personalistu",
    verifyGdprHint: "Kvůli ochraně osobních údajů nezveřejňujeme seznam certifikovaných jednotlivců. Zadej příjmení nebo číslo certifikátu — pokud osoba existuje, uvidíš veřejnou vizitku.",
    verifyPlaceholder: "Příjmení nebo číslo certifikátu…",
    verifyMinChars: "Zadej alespoň 3 znaky · max 8 výsledků",
    verifyEmptyPro: "Žádný profesionál se neshoduje",
    verifyEmptyRec: "Žádný personalista se neshoduje",
    verifyEmptyDesc: "Zkus jiný tvar jména nebo zkontroluj správnost čísla certifikátu.",
    verifiedBadge: "OVĚŘENÝ",
    sectorIndustry: "Průmysl & Výroba",
    sectorServices: "Služby",
    sectorUnclassified: "Nezařazené",
    sectorBack: "Zpět na výběr",
    sectorPromptTitle: "Vyberte oblast podnikání",
    sectorPromptDesc: "Certifikované firmy jsme rozdělili podle sektoru, abyste rychle našli tu správnou.",
    emptyRecTitle: "Zatím žádní certifikovaní personalisté",
    emptyRecDesc: "Personalisté přibývají postupně, jak získávají certifikát SauberEko®.",
    certNo: "Certifikát č.",
    certifiedSince: "Certifikovaný od",
    extraCerts: "dalších certifikátů",
    viewProfile: "Profil",
    viewCert: "Zobrazit certifikát",
    visitWeb: "Web",
    closeCert: "Zavřít",
    gateTitle: "Síň slávy je dostupná pouze pro registrované členy",
    gateBody: "Veřejný registr SauberEko® certifikovaných profesionálů a firem je viditelný pouze pro přihlášené členy Tigiku. Registrace je zdarma a zabere pár vteřin.",
    gateOfficialLink: "Oficiální registr je dostupný i na",
    gateRegister: "Zaregistrovat se",
    gateLogin: "Přihlásit se",
    paywallTitle: "Síň slávy jen pro členy ACTIVE+",
    paywallBody: "Síň slávy SauberEko® je výsadou platících členů Tigiku. Aktivuj si ACTIVE (€3,99/týden) nebo PRO (€6,99/měsíc) a získáš přístup k celé databázi certifikovaných profesionálů, firem i personalistů.",
    paywallBtn: "Aktivovat předplatné",
    paywallSecondaryBtn: "Získat certifikát zdarma",
  },
  de: {
    back: "Zurück",
    eyebrow: "Hall of Fame",
    title: "SauberEko® Zertifizierte",
    subtitle: "Öffentliches Register von Profis und Unternehmen, die die strenge SauberEko®-Verifizierung bestanden haben. Jedes SauberEko®-Zertifikat ist an eine eindeutige Registernummer gebunden.",
    tabPros: "Profis",
    tabCompanies: "Unternehmen",
    tabRecognitions: "Auszeichnungen",
    tabQualified: "Geprüft & Absolventen",
    qualifiedIntro: "Geprüfte HR-/Personalvermittler und Absolventen des Programms „Work, learn and earn©“ — praxisgeprüfte Profis.",
    emptyQualifiedTitle: "Noch keine Einträge",
    emptyQualifiedDesc: "Sobald Mentor-Audits und HR-Zertifizierungen eintreffen, erscheinen sie hier.",
    graduateBadge: "🎓 Absolvent",
    cardIdLabel: "Card ID",
    recognitionsIntro: "Auszeichnungen und Ehrungen, die von der Marke SauberEko® vergeben werden — Persönlichkeiten, Unternehmen, Lebenswerk. Öffentliches Register ohne Paywall.",
    emptyRecognitionsTitle: "Noch keine Auszeichnungen",
    emptyRecognitionsDesc: "Sobald die Marke SauberEko® die erste Ehrung vergibt, erscheint sie hier.",
    recognitionAwarded: "Verliehen",
    recognitionCategory: "Kategorie",
    emptyProsTitle: "Noch keine zertifizierten Profis",
    emptyProsDesc: "Die Hall of Fame füllt sich nach und nach mit neuen Sa.Eko.®-Zertifikatsinhabern.",
    emptyCompTitle: "Noch keine zertifizierten Unternehmen",
    emptyCompDesc: "Unternehmen werden hinzugefügt, sobald sie die SauberEko®-Zertifizierung erhalten.",
    verifyEyebrow: "Zertifikatsprüfung",
    verifyTitlePro: "Fachkraft überprüfen",
    verifyTitleRec: "Personalvermittler überprüfen",
    verifyGdprHint: "Zum Schutz personenbezogener Daten führen wir keine öffentliche Liste zertifizierter Personen. Gib den Nachnamen oder die Zertifikatsnummer ein — falls die Person existiert, siehst du deren öffentliche Karte.",
    verifyPlaceholder: "Nachname oder Zertifikatsnummer…",
    verifyMinChars: "Mindestens 3 Zeichen · max. 8 Ergebnisse",
    verifyEmptyPro: "Keine passende Fachkraft gefunden",
    verifyEmptyRec: "Kein passender Personalvermittler",
    verifyEmptyDesc: "Versuche eine andere Schreibweise oder überprüfe die Zertifikatsnummer.",
    verifiedBadge: "VERIFIZIERT",
    sectorIndustry: "Industrie & Fertigung",
    sectorServices: "Dienstleistungen",
    sectorUnclassified: "Nicht zugeordnet",
    sectorBack: "Zurück zur Auswahl",
    sectorPromptTitle: "Wählen Sie einen Geschäftsbereich",
    sectorPromptDesc: "Wir haben zertifizierte Unternehmen nach Branchen unterteilt, damit Sie schnell die richtige finden.",
    emptyRecTitle: "Noch keine zertifizierten Recruiter",
    emptyRecDesc: "Recruiter & HR-Manager werden hinzugefügt, sobald sie die SauberEko®-Zertifizierung erhalten.",
    certNo: "Zertifikat-Nr.",
    certifiedSince: "Zertifiziert seit",
    extraCerts: "weitere Zertifikate",
    viewProfile: "Profil",
    viewCert: "Zertifikat ansehen",
    visitWeb: "Webseite",
    closeCert: "Schließen",
    gateTitle: "Hall of Fame nur für registrierte Mitglieder",
    gateBody: "Das öffentliche Register von SauberEko® zertifizierten Profis und Unternehmen ist nur für eingeloggte Tigik-Mitglieder sichtbar. Die Registrierung ist kostenlos und dauert nur wenige Sekunden.",
    gateOfficialLink: "Das offizielle Register ist auch verfügbar unter",
    gateRegister: "Registrieren",
    gateLogin: "Anmelden",
    paywallTitle: "Hall of Fame nur für ACTIVE+ Mitglieder",
    paywallBody: "Die SauberEko® Hall of Fame ist bezahlten Tigik-Mitgliedern vorbehalten. Aktiviere ACTIVE (€3,99/Woche) oder PRO (€6,99/Monat) und erhalte Zugang zur kompletten Datenbank zertifizierter Profis, Unternehmen und Recruiter.",
    paywallBtn: "Abo aktivieren",
    paywallSecondaryBtn: "Zertifizierung anfordern (gratis)",
  },
  pl: {
    back: "Wstecz",
    eyebrow: "Sala sławy",
    title: "Certyfikowani SauberEko®",
    subtitle: "Publiczny rejestr profesjonalistów i firm, którzy przeszli rygorystyczną weryfikację SauberEko®. Każdy certyfikat marki SauberEko® jest powiązany z unikalnym numerem ewidencyjnym.",
    tabPros: "Profesjonaliści",
    tabCompanies: "Firmy",
    tabRecognitions: "Wyróżnienia",
    tabQualified: "Zweryfikowani i Absolwenci",
    qualifiedIntro: "Zweryfikowani rekruterzy HR i absolwenci programu „Work, learn and earn©“ — profesjonaliści zweryfikowani w praktyce.",
    emptyQualifiedTitle: "Brak wpisów",
    emptyQualifiedDesc: "Wraz z przybywaniem audytów mentorów i certyfikacji HR pojawią się tutaj.",
    graduateBadge: "🎓 Absolwent",
    cardIdLabel: "Card ID",
    recognitionsIntro: "Wyróżnienia i odznaczenia przyznane przez markę SauberEko® — osobowości, firmy, dzieło życia. Rejestr publiczny, bez paywall-a.",
    emptyRecognitionsTitle: "Brak wyróżnień",
    emptyRecognitionsDesc: "Gdy marka SauberEko® przyzna pierwsze wyróżnienie, pojawi się tutaj.",
    recognitionAwarded: "Przyznano",
    recognitionCategory: "Kategoria",
    emptyProsTitle: "Brak certyfikowanych profesjonalistów",
    emptyProsDesc: "Sala sławy zapełnia się stopniowo wraz z nowymi posiadaczami certyfikatu Sa.Eko.®",
    emptyCompTitle: "Brak certyfikowanych firm",
    emptyCompDesc: "Firmy są dodawane w miarę uzyskiwania certyfikatu SauberEko®.",
    verifyEyebrow: "Weryfikacja certyfikatu",
    verifyTitlePro: "Zweryfikuj profesjonalistę",
    verifyTitleRec: "Zweryfikuj rekrutera",
    verifyGdprHint: "W trosce o dane osobowe nie publikujemy listy certyfikowanych osób. Wpisz nazwisko lub numer certyfikatu — jeśli osoba istnieje, zobaczysz publiczną wizytówkę.",
    verifyPlaceholder: "Nazwisko lub numer certyfikatu…",
    verifyMinChars: "Wpisz co najmniej 3 znaki · maks. 8 wyników",
    verifyEmptyPro: "Żaden profesjonalista nie pasuje",
    verifyEmptyRec: "Żaden rekruter nie pasuje",
    verifyEmptyDesc: "Spróbuj inaczej wpisać imię lub sprawdź numer certyfikatu.",
    verifiedBadge: "ZWERYFIKOWANY",
    sectorIndustry: "Przemysł & Produkcja",
    sectorServices: "Usługi",
    sectorUnclassified: "Niesklasyfikowane",
    sectorBack: "Powrót do wyboru",
    sectorPromptTitle: "Wybierz branżę",
    sectorPromptDesc: "Podzieliliśmy certyfikowane firmy według sektorów, abyś szybko znalazł tę właściwą.",
    emptyRecTitle: "Brak certyfikowanych rekruterów",
    emptyRecDesc: "Rekruterzy i HR są dodawani w miarę uzyskiwania certyfikatu SauberEko®.",
    certNo: "Certyfikat nr",
    certifiedSince: "Certyfikowany od",
    extraCerts: "dodatkowych certyfikatów",
    viewProfile: "Profil",
    viewCert: "Zobacz certyfikat",
    visitWeb: "Strona",
    closeCert: "Zamknij",
    gateTitle: "Galeria sław jest dostępna tylko dla zarejestrowanych członków",
    gateBody: "Publiczny rejestr certyfikowanych przez SauberEko® profesjonalistów i firm jest widoczny tylko dla zalogowanych członków Tigika. Rejestracja jest bezpłatna i zajmuje kilka sekund.",
    gateOfficialLink: "Oficjalny rejestr jest również dostępny na",
    gateRegister: "Zarejestruj się",
    gateLogin: "Zaloguj się",
  },
  fr: {
    back: "Retour",
    eyebrow: "Hall of Fame",
    title: "Certifiés SauberEko®",
    subtitle: "Registre public des professionnels et entreprises ayant passé la vérification stricte SauberEko®. Chaque certificat de marque SauberEko® est lié à un numéro d'enregistrement unique.",
    tabPros: "Professionnels",
    tabCompanies: "Entreprises",
    tabRecognitions: "Distinctions",
    tabQualified: "Vérifiés & Diplômés",
    qualifiedIntro: "RH vérifiés et diplômés du programme « Work, learn and earn© » — des pros validés sur le terrain.",
    emptyQualifiedTitle: "Aucune entrée pour l'instant",
    emptyQualifiedDesc: "Au fur et à mesure des audits mentor et vérifications RH, elles apparaîtront ici.",
    graduateBadge: "🎓 Diplômé",
    cardIdLabel: "Card ID",
    recognitionsIntro: "Récompenses et distinctions décernées par la marque SauberEko® — Personnalités, Entreprises, Œuvre d'une vie. Registre public sans paywall.",
    emptyRecognitionsTitle: "Aucune distinction pour l'instant",
    emptyRecognitionsDesc: "Dès que la marque SauberEko® décernera sa première distinction, elle apparaîtra ici.",
    recognitionAwarded: "Décerné",
    recognitionCategory: "Catégorie",
    emptyProsTitle: "Aucun professionnel certifié pour l'instant",
    emptyProsDesc: "Le Hall of Fame se remplit progressivement, à mesure que les détenteurs de certificat Sa.Eko.® s'ajoutent.",
    emptyCompTitle: "Aucune entreprise certifiée pour l'instant",
    emptyCompDesc: "Elles s'ajoutent au fur et à mesure que les entreprises obtiennent leur certificat SauberEko®.",
    verifyEyebrow: "Vérification de certificat",
    verifyTitlePro: "Vérifier un professionnel",
    verifyTitleRec: "Vérifier un recruteur",
    verifyGdprHint: "Pour protéger les données personnelles, nous n'affichons pas de liste publique des particuliers certifiés. Saisis le nom de famille ou le numéro de certificat — si la personne existe, tu verras sa carte publique.",
    verifyPlaceholder: "Nom ou numéro de certificat…",
    verifyMinChars: "Saisis au moins 3 caractères · max 8 résultats",
    verifyEmptyPro: "Aucun professionnel ne correspond",
    verifyEmptyRec: "Aucun recruteur ne correspond",
    verifyEmptyDesc: "Essaie une autre forme du nom ou vérifie l'exactitude du numéro de certificat.",
    verifiedBadge: "VÉRIFIÉ",
    sectorIndustry: "Industrie & Fabrication",
    sectorServices: "Services",
    sectorUnclassified: "Non classé",
    sectorBack: "Retour à la sélection",
    sectorPromptTitle: "Choisis le secteur d'activité",
    sectorPromptDesc: "Nous avons réparti les entreprises certifiées par secteur pour que tu trouves rapidement celle qu'il te faut.",
    emptyRecTitle: "Aucun recruteur certifié pour l'instant",
    emptyRecDesc: "Ils s'ajoutent progressivement, à mesure que les responsables RH obtiennent leur certificat SauberEko®.",
    certNo: "Certificat n°",
    certifiedSince: "Certifié depuis",
    extraCerts: "certificats supplémentaires",
    viewProfile: "Profil",
    viewCert: "Voir le certificat",
    visitWeb: "Site web",
    closeCert: "Fermer",
    gateTitle: "Le Hall of Fame est réservé aux membres enregistrés",
    gateBody: "Le registre public SauberEko® des professionnels et entreprises certifiés n'est visible que pour les membres connectés de Tigik. L'inscription est gratuite et ne prend que quelques secondes.",
    gateOfficialLink: "Le registre officiel est également disponible sur",
    gateRegister: "S'inscrire",
    gateLogin: "Se connecter",
    paywallTitle: "Le Hall of Fame est réservé aux membres ACTIVE et supérieurs",
    paywallBody: "Le Hall of Fame SauberEko® est un privilège des membres payants Tigik. Active ton abonnement ACTIVE (3,99 €/semaine) ou PRO (6,99 €/mois) et accède à toute la base de pros, entreprises et recruteurs certifiés.",
    paywallBtn: "Activer l'abonnement",
    paywallSecondaryBtn: "Obtenir un certificat gratuit",
  },
};

function CertificateModal({ items, onClose, t }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [items]);
  if (!items || items.length === 0) return null;
  const list = items;
  const cur = list[idx];
  const next = () => setIdx((i) => (i + 1) % list.length);
  const prev = () => setIdx((i) => (i - 1 + list.length) % list.length);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid="cert-photo-modal">
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 border border-zinc-700 hover:border-gold-500 text-zinc-300 hover:text-white flex items-center justify-center z-10" aria-label={t.closeCert}>
        <X className="w-5 h-5" />
      </button>
      {list.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 border border-zinc-700 bg-black/60 hover:border-gold-500 text-zinc-300 hover:text-white flex items-center justify-center z-10"
            aria-label="prev"
            data-testid="cert-modal-prev"
          >
            ‹
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 border border-zinc-700 bg-black/60 hover:border-gold-500 text-zinc-300 hover:text-white flex items-center justify-center z-10"
            aria-label="next"
            data-testid="cert-modal-next"
          >
            ›
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/60 border border-zinc-700 text-xs font-mono uppercase tracking-widest text-gold-400 z-10" data-testid="cert-modal-counter">
            {idx + 1} / {list.length}
          </div>
        </>
      )}
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={cur.photo} alt={cur.caption || "Certificate"} className="max-w-full max-h-[80vh] object-contain border border-gold-700/40 mx-auto" />
        {cur.caption && (
          <p className="text-center text-zinc-300 mt-3 font-mono text-sm px-4">{cur.caption}</p>
        )}
      </div>
    </div>
  );
}

function ProfessionalCard({ u, t, onOpenPhotoFetch }) {
  const cardKey = u.user_id || u.manual_id || u.name;
  // Compat: `certificate_photo_available` (slim mode) OR legacy `certificate_photo`
  const hasCert = u.certificate_photo_available || !!u.certificate_photo;
  const extraCount = u.certificate_photos_extra_count ?? (u.certificate_photos_extra?.length || 0);
  const totalPhotos = (hasCert ? 1 : 0) + extraCount;
  return (
    <div data-testid={`wall-pro-${cardKey}`} className="border border-gold-700/30 bg-gradient-to-br from-zinc-950 to-black p-5 hover:border-gold-500/60 transition-colors">
      <div className="flex items-start gap-3 mb-4">
        {u.picture ? (
          <img src={u.picture} alt={u.name} className="w-14 h-14 object-cover border border-zinc-800" />
        ) : (
          <div className="w-14 h-14 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
            <span className="font-display font-bold text-gold-400 text-xl">{u.name?.[0] || "—"}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-white truncate">{u.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500 mt-0.5">SAUBEREKO® CERTIFIED</div>
        </div>
      </div>
      <div className="space-y-1.5 text-xs text-zinc-400">
        <div className="flex items-center gap-2"><Award className="w-3 h-3 text-gold-500" /> <span className="font-mono">{t.certNo} {u.certificate_no || "—"}</span></div>
        {u.specialization && <div className="flex items-center gap-2"><Briefcase className="w-3 h-3 text-zinc-600" /> {u.specialization}</div>}
        {u.company && <div className="flex items-center gap-2"><Briefcase className="w-3 h-3 text-zinc-600" /> {u.company}</div>}
        {(u.city || u.country) && <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-zinc-600" /> {[u.city, u.country].filter(Boolean).join(", ")}</div>}
        {extraCount > 0 && (
          <div className="flex items-center gap-2 text-gold-400/80">
            <Award className="w-3 h-3 text-gold-500" />
            <span className="font-mono">+{extraCount} {t.extraCerts}</span>
          </div>
        )}
      </div>
      {(hasCert || u.user_id || extraCount > 0) && (
        <div className="mt-4 pt-3 border-t border-zinc-800/60 flex gap-2">
          {(hasCert || extraCount > 0) && (
            <button
              onClick={() => onOpenPhotoFetch(u.user_id ? "pro-user" : "pro-manual", u.user_id || u.manual_id)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20"
              data-testid={`wall-pro-cert-view-${cardKey}`}
            >
              <ImageIcon className="w-3 h-3" /> {t.viewCert}
              {totalPhotos > 1 && (
                <span className="ml-1 text-[10px] text-gold-500/80">×{totalPhotos}</span>
              )}
            </button>
          )}
          {u.user_id && (
            <Link to={`/u/${u.user_id}`} className="flex-1 inline-flex items-center justify-center text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-zinc-700 text-zinc-300 hover:border-zinc-500">
              {t.viewProfile}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function CompanyCard({ c, t, onOpenPhotoFetch }) {
  const hasCert = c.certificate_photo_available || !!c.certificate_photo;
  return (
    <div data-testid={`wall-comp-${c.id}`} className="border border-gold-700/30 bg-gradient-to-br from-zinc-950 to-black p-5 hover:border-gold-500/60 transition-colors">
      <div className="flex items-start gap-3 mb-4">
        {c.logo_base64 ? (
          <img src={c.logo_base64} alt={c.name} className="w-14 h-14 object-contain bg-white/5 border border-zinc-800" />
        ) : (
          <div className="w-14 h-14 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-gold-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-white truncate">{c.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500 mt-0.5">SAUBEREKO® CERTIFIED · COMPANY</div>
          {c.sector && (
            <div className="mt-1 inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-gold-800/50 text-gold-400/90">
              {c.sector === "industry" ? `🏭 ${t.sectorIndustry || "Priemysel"}` : c.sector === "services" ? `🛠️ ${t.sectorServices || "Služby"}` : c.sector}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-1.5 text-xs text-zinc-400">
        {c.ico && <div className="font-mono" data-testid="wall-comp-ico">IČO {c.ico}</div>}
        {c.certificate_no && <div className="flex items-center gap-2"><Award className="w-3 h-3 text-gold-500" /> <span className="font-mono">{t.certNo} {c.certificate_no}</span></div>}
        {(c.city || c.country) && <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-zinc-600" /> {[c.city, c.country].filter(Boolean).join(", ")}</div>}
        {c.description && <div className="text-zinc-500 leading-relaxed pt-1">{c.description}</div>}
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-800/60 flex gap-2">
        {hasCert && (
          <button onClick={() => onOpenPhotoFetch("company", c.id)} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20" data-testid={`wall-comp-cert-view-${c.id}`}>
            <ImageIcon className="w-3 h-3" /> {t.viewCert}
          </button>
        )}
        {c.website && (
          <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-zinc-700 text-zinc-300 hover:border-zinc-500">
            <Globe className="w-3 h-3" /> {t.visitWeb}
          </a>
        )}
      </div>
    </div>
  );
}

function RecruiterCard({ r, t, onOpenPhotoFetch }) {
  const hasCert = r.certificate_photo_available || !!r.certificate_photo;
  return (
    <div data-testid={`wall-rec-${r.id}`} className="border border-gold-700/30 bg-gradient-to-br from-zinc-950 to-black p-5 hover:border-gold-500/60 transition-colors">
      <div className="flex items-start gap-3 mb-4">
        {r.picture ? (
          <img src={r.picture} alt={r.name} className="w-14 h-14 object-cover border border-zinc-800" />
        ) : (
          <div className="w-14 h-14 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
            <UserSearch className="w-7 h-7 text-gold-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-white truncate">{r.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500 mt-0.5">SAUBEREKO® CERTIFIED · NÁBOR & HR</div>
        </div>
      </div>
      <div className="space-y-1.5 text-xs text-zinc-400">
        {r.certificate_no && <div className="flex items-center gap-2"><Award className="w-3 h-3 text-gold-500" /> <span className="font-mono">{t.certNo} {r.certificate_no}</span></div>}
        {r.specialization && <div className="flex items-center gap-2"><Briefcase className="w-3 h-3 text-zinc-600" /> {r.specialization}</div>}
        {r.company && <div className="flex items-center gap-2"><Building2 className="w-3 h-3 text-zinc-600" /> {r.company}</div>}
        {(r.city || r.country) && <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-zinc-600" /> {[r.city, r.country].filter(Boolean).join(", ")}</div>}
        {r.description && <div className="text-zinc-500 leading-relaxed pt-1">{r.description}</div>}
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-800/60 flex gap-2 flex-wrap">
        {hasCert && (
          <button onClick={() => onOpenPhotoFetch("recruiter", r.id)} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20" data-testid={`wall-rec-cert-view-${r.id}`}>
            <ImageIcon className="w-3 h-3" /> {t.viewCert}
          </button>
        )}
        {r.linkedin && (
          <a href={r.linkedin.startsWith("http") ? r.linkedin : `https://${r.linkedin}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-blue-700/40 text-blue-300 hover:bg-blue-950/30" data-testid={`wall-rec-linkedin-${r.id}`}>
            <Linkedin className="w-3 h-3" /> LinkedIn
          </a>
        )}
        {r.website && (
          <a href={r.website.startsWith("http") ? r.website : `https://${r.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-zinc-700 text-zinc-300 hover:border-zinc-500">
            <Globe className="w-3 h-3" /> {t.visitWeb}
          </a>
        )}
        {r.email && (
          <a href={`mailto:${r.email}`} className="inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-zinc-700 text-zinc-300 hover:border-zinc-500">
            <Mail className="w-3 h-3" /> E-mail
          </a>
        )}
      </div>
    </div>
  );
}

function VerifySearchPanel({ t, query, onQueryChange, loading, results, done, kind, texts = {}, count, countLabel, renderCard }) {
  const { lang } = useLang();
  const titleKey = kind === "recruiter" ? "verifyTitleRec" : "verifyTitlePro";
  const emptyKey = kind === "recruiter" ? "verifyEmptyRec" : "verifyEmptyPro";
  return (
    <section className="mt-8" data-testid={`wall-verify-${kind}`}>
      <div className="border border-gold-700/30 bg-gradient-to-br from-zinc-950 to-black p-6 sm:p-8">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-gold-400 mb-3">
          <ShieldCheck className="w-3 h-3" /> {t.verifyEyebrow}
        </div>
        <h2 className="font-display font-black text-2xl sm:text-3xl text-white leading-tight mb-3">
          {texts.title || t[titleKey]}
        </h2>
        {count != null && (
          <div className="text-sm text-gold-300 font-mono mb-3" data-testid={`wall-count-${kind}`}>
            <b className="text-2xl text-gold-400">{count}</b> {countLabel}
          </div>
        )}
        <p className="text-sm text-zinc-400 max-w-2xl mb-5">{texts.hint || t.verifyGdprHint}</p>
        <div className="relative">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            autoFocus
            placeholder={texts.placeholder || t.verifyPlaceholder}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            className="wb-input w-full pl-10 pr-10 py-3 text-base font-mono"
            data-testid="wall-verify-input"
          />
          {loading && (
            <Loader2 className="w-4 h-4 text-gold-400 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />
          )}
        </div>
        <p className="text-[10px] text-zinc-600 mt-2 font-mono uppercase tracking-widest">
          {t.verifyMinChars}
        </p>
      </div>

      {done && results.length === 0 && (query || "").trim().length >= 3 && !loading && (
        <div className="mt-6 text-center py-10 border border-zinc-800 bg-zinc-950" data-testid="wall-verify-empty">
          <UserSearch className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
          <div className="font-display font-bold text-lg text-zinc-300 mb-1">{texts.empty || t[emptyKey]}</div>
          <p className="text-xs text-zinc-500 max-w-md mx-auto">{t.verifyEmptyDesc}</p>
        </div>
      )}

      {results.length > 0 && (
        <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="wall-verify-results">
          {results.map((r, idx) => (
            renderCard ? <React.Fragment key={r.id || idx}>{renderCard(r)}</React.Fragment>
              : <VerifyResultCard key={r.certificate_no || r.name || idx} r={r} t={t} />
          ))}
        </div>
      )}
      {results.length > 0 && (
        <a href="https://saubereko.eu/hall-of-fame" target="_blank" rel="noopener noreferrer" data-testid="wall-saubereko-profile-btn"
           className="mt-5 inline-flex items-center gap-2 px-5 py-3 bg-gold-500 hover:bg-gold-400 text-black font-mono uppercase tracking-wider text-xs font-bold transition-colors">
          <Trophy className="w-3.5 h-3.5" /> {HOF_BTN[lang] || HOF_BTN.sk} →
        </a>
      )}
    </section>
  );
}

function VerifyResultCard({ r, t }) {
  const { lang } = useLang();
  return (
    <div className="border border-gold-700/30 bg-gradient-to-br from-zinc-950 to-black p-5 hover:border-gold-500/60 transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-gold-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-white truncate">{r.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500 mt-0.5">
            {r.kind === "recruiter" ? "SAUBEREKO® CERTIFIED · HR" : "SAUBEREKO® CERTIFIED"}
          </div>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-green-800 text-green-400 whitespace-nowrap">
          ✓ {t.verifiedBadge}
        </span>
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-800/60 space-y-1.5 text-xs text-zinc-400">
        <div className="flex items-center gap-2">
          <Award className="w-3 h-3 text-gold-500" />
          <span className="font-mono">{t.certNo} {r.certificate_no || "—"}</span>
        </div>
        {r.certified_since && (
          <div className="text-zinc-500 text-[11px]">
            {t.certifiedSince}: {new Date(r.certified_since).toLocaleDateString()}
          </div>
        )}
        {((r.skills && r.skills.length > 0) || r.skills_other) && (
          <div className="pt-1">
            <SkillsBadge skills={r.skills} skillsOther={r.skills_other} lang={lang} />
          </div>
        )}
        {r.mentor && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border border-gold-500 bg-gold-950/40 text-gold-300"
                data-testid="hof-mentor-badge">
            🎓 {lang === "en" ? "Mentor" : lang === "de" ? "Mentor" : lang === "pl" ? "Mentor" : lang === "cs" ? "Mentor" : "Mentor"}
          </span>
        )}
        {r.website && (
          <a href={r.website.startsWith("http") ? r.website : `https://${r.website}`}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 mt-1">
            <Globe className="w-3 h-3" /> {t.visitWeb}
          </a>
        )}
      </div>
    </div>
  );
}

function RecognitionCard({ r, t }) {
  const [showAward, setShowAward] = React.useState(false);
  const displayDate = r.awarded_date
    ? (() => { try { return new Date(r.awarded_date).toLocaleDateString(); } catch { return r.awarded_date; } })()
    : null;
  return (
    <div id={`recognition-${r.id}`} className="border border-gold-700/30 bg-gradient-to-br from-zinc-950 to-black overflow-hidden hover:border-gold-500/60 transition-colors" data-testid={`wall-recognition-${r.id}`}>
      {r.award_image && (
        <button
          onClick={() => setShowAward(true)}
          className="w-full aspect-video bg-black overflow-hidden relative group"
          data-testid={`wall-recognition-award-${r.id}`}
        >
          <img src={r.award_image} alt={r.recognition_title} className="w-full h-full object-contain" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 text-[10px] font-mono uppercase tracking-widest text-gold-300 border border-gold-500 px-2 py-1">
              {t.viewCert || "Zobraziť"}
            </span>
          </div>
        </button>
      )}
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          {r.picture ? (
            <img src={r.picture} alt={r.name} className="w-12 h-12 object-cover border border-gold-700/40" />
          ) : (
            <div className="w-12 h-12 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center flex-shrink-0">
              <Trophy className="w-6 h-6 text-gold-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500 mb-0.5">
              🏆 SAUBEREKO® {t.recognitionAwarded?.toUpperCase() || "AWARDED"}
            </div>
            <div className="font-display font-bold text-base text-white leading-tight">
              {r.recognition_title}
            </div>
          </div>
        </div>
        <div className="border-t border-zinc-800/60 pt-3 space-y-1.5 text-xs text-zinc-400">
          <div className="text-gold-300 font-display font-bold text-sm">{r.name}</div>
          {r.category && (
            <div className="text-[11px] text-zinc-500">
              <span className="font-mono uppercase tracking-widest text-zinc-600">{t.recognitionCategory}:</span> {r.category}
            </div>
          )}
          {(r.city || r.country) && (
            <div className="text-[11px] text-zinc-500">
              📍 {[r.city, r.country].filter(Boolean).join(", ")}
            </div>
          )}
          {displayDate && (
            <div className="text-[11px] text-zinc-500">
              {t.recognitionAwarded}: {displayDate}
            </div>
          )}
          {r.description && (
            <p className="text-[11px] text-zinc-400 leading-relaxed pt-2 border-t border-zinc-800/60 mt-2">
              {r.description}
            </p>
          )}
          <div className="flex flex-wrap gap-3 pt-2">
            {r.website && (
              <a href={r.website.startsWith("http") ? r.website : `https://${r.website}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-[11px]">
                <Globe className="w-3 h-3" /> {t.visitWeb}
              </a>
            )}
            {r.social && (
              <a href={r.social.startsWith("http") ? r.social : `https://${r.social}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-[11px]">
                <Globe className="w-3 h-3" /> Social
              </a>
            )}
          </div>
        </div>
      </div>
      {showAward && r.award_image && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowAward(false)}>
          <img src={r.award_image} alt={r.recognition_title} className="max-w-full max-h-[90vh] object-contain" />
          <button
            onClick={() => setShowAward(false)}
            className="absolute top-4 right-4 text-gold-400 hover:text-white p-2 border border-gold-700/50"
            aria-label={t.closeCert}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

function QualifiedCard({ q, t }) {
  const isGrad = !!q.graduate;
  const displayDate = q.certified_since
    ? (() => { try { return new Date(q.certified_since).toLocaleDateString(); } catch { return q.certified_since; } })()
    : null;
  return (
    <div className={`border overflow-hidden hover:border-gold-500/60 transition-colors ${isGrad ? "border-gold-500/60 bg-gradient-to-br from-gold-950/30 to-black" : "border-gold-700/30 bg-gradient-to-br from-zinc-950 to-black"}`}
         data-testid={`wall-qualified-${q.id}`}>
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          {q.picture ? (
            <img src={q.picture} alt={q.name} className="w-12 h-12 object-cover border border-gold-700/40" />
          ) : (
            <div className="w-12 h-12 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-gold-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500 mb-0.5">
              SAUBEREKO® CERTIFIED · HR
            </div>
            <div className="font-display font-bold text-base text-white leading-tight truncate">
              {q.name}
            </div>
            {q.specialization && (
              <div className="text-[11px] text-zinc-400 truncate">{q.specialization}</div>
            )}
          </div>
        </div>
        {isGrad && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-gold-500 bg-gold-950/50 text-gold-300"
                  data-testid="wall-qualified-graduate-badge">
              {t.graduateBadge}
              {q.program ? ` · ${q.program}` : ""}
            </span>
          </div>
        )}
        <div className="border-t border-zinc-800/60 pt-3 space-y-1.5 text-xs text-zinc-400">
          {isGrad && q.card_id ? (
            <div className="flex items-center gap-2">
              <Award className="w-3 h-3 text-gold-500" />
              <span className="font-mono text-gold-300">{t.cardIdLabel}: {q.card_id}</span>
            </div>
          ) : q.certificate_no ? (
            <div className="flex items-center gap-2">
              <Award className="w-3 h-3 text-gold-500" />
              <span className="font-mono">{t.certNo} {q.certificate_no}</span>
            </div>
          ) : null}
          {(q.city || q.country) && (
            <div className="text-[11px] text-zinc-500">📍 {[q.city, q.country].filter(Boolean).join(", ")}</div>
          )}
          {q.company && (
            <div className="text-[11px] text-zinc-500">🏢 {q.company}</div>
          )}
          {displayDate && (
            <div className="text-[11px] text-zinc-500">{t.certifiedSince}: {displayDate}</div>
          )}
          {q.description && (
            <p className="text-[11px] text-zinc-400 leading-relaxed pt-2 border-t border-zinc-800/60 mt-2">
              {q.description}
            </p>
          )}
          <div className="flex flex-wrap gap-3 pt-2">
            {q.website && (
              <a href={q.website.startsWith("http") ? q.website : `https://${q.website}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-[11px]">
                <Globe className="w-3 h-3" /> {t.visitWeb}
              </a>
            )}
            {q.linkedin && (
              <a href={q.linkedin.startsWith("http") ? q.linkedin : `https://${q.linkedin}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-[11px]">
                <Linkedin className="w-3 h-3" /> LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const HOF_BTN = { sk: "Pozri celý profil na saubereko.eu", en: "See the full profile on saubereko.eu", cs: "Zobrazit celý profil na saubereko.eu", de: "Vollständiges Profil auf saubereko.eu", pl: "Zobacz pełny profil na saubereko.eu", fr: "Voir le profil complet sur saubereko.eu" };

const COMP_TR = {
  sk: { hint: "Zadaj názov firmy alebo IČO a over, či má platný certifikát SauberEko®.", title: "Overiť firmu", placeholder: "Názov firmy alebo IČO…", empty: "Firma sa nenašla", pros: "certifikovaných profesionálov", comps: "certifikovaných firiem", main: "Kompletná Sieň slávy SauberEko® je na" },
  en: { hint: "Enter a company name or company ID (IČO) to check whether it holds a valid SauberEko® certificate.", title: "Verify a company", placeholder: "Company name or ID (IČO)…", empty: "Company not found", pros: "certified professionals", comps: "certified companies", main: "The complete SauberEko® Hall of Fame is on" },
  cs: { hint: "Zadej název firmy nebo IČO a ověř, zda má platný certifikát SauberEko®.", title: "Ověřit firmu", placeholder: "Název firmy nebo IČO…", empty: "Firma nenalezena", pros: "certifikovaných profesionálů", comps: "certifikovaných firem", main: "Kompletní Síň slávy SauberEko® je na" },
  de: { hint: "Gib einen Firmennamen oder die Firmen-ID (IČO) ein und prüfe, ob sie ein gültiges SauberEko®-Zertifikat hat.", title: "Firma prüfen", placeholder: "Firmenname oder Firmen-ID…", empty: "Firma nicht gefunden", pros: "zertifizierte Fachkräfte", comps: "zertifizierte Firmen", main: "Die komplette SauberEko® Hall of Fame findest du auf" },
  pl: { hint: "Wpisz nazwę firmy lub NIP/IČO i sprawdź, czy ma ważny certyfikat SauberEko®.", title: "Zweryfikuj firmę", placeholder: "Nazwa firmy lub NIP/IČO…", empty: "Nie znaleziono firmy", pros: "certyfikowanych profesjonalistów", comps: "certyfikowanych firm", main: "Pełna Galeria Sław SauberEko® jest na" },
  fr: { hint: "Saisis le nom ou l'identifiant (IČO) d'une entreprise pour vérifier qu'elle a un certificat SauberEko® valide.", title: "Vérifier une entreprise", placeholder: "Nom ou identifiant (IČO)…", empty: "Entreprise introuvable", pros: "professionnels certifiés", comps: "entreprises certifiées", main: "Le Hall of Fame SauberEko® complet est sur" },
};

export default function WallOfFame() {
  const { lang } = useLang();
  const { user, loading: authLoading } = useAuth();
  const t = TR[lang] || TR.sk;
  const ct = COMP_TR[lang] || COMP_TR.sk;
  const [data, setData] = React.useState({ professionals: [], companies: [], recruiters: [], professionals_count: 0, companies_count: 0, recruiters_count: 0 });
  const [supporters, setSupporters] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openPhoto, setOpenPhoto] = React.useState(null);
  const [photoLoading, setPhotoLoading] = React.useState(false);
  const [tab, setTab] = React.useState("pros"); // "pros" | "companies" | "recognitions"
  // Iteration 74: Individuals no longer listed publicly — targeted verify search only
  const [verifyQ, setVerifyQ] = React.useState("");
  const [verifyResults, setVerifyResults] = React.useState([]);
  const [verifyLoading, setVerifyLoading] = React.useState(false);
  const [verifyDone, setVerifyDone] = React.useState(false);

  React.useEffect(() => {
    api.get("/hof/supporters")
      .then((r) => setSupporters(r.data?.items || []))
      .catch(() => setSupporters([]));
  }, []);

  React.useEffect(() => {
    if (tab !== "pros" && tab !== "companies") return;
    const q = (verifyQ || "").trim();
    if (q.length < 3) { setVerifyResults([]); setVerifyDone(false); return; }
    setVerifyLoading(true);
    const kind = tab === "companies" ? "company" : "professional";
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const r = await api.get(`/hof/verify?q=${encodeURIComponent(q)}&kind=${kind}`, { signal: ctrl.signal });
        setVerifyResults(r.data?.results || []);
        setVerifyDone(true);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          console.error("verify search failed", e);
          setVerifyResults([]);
          setVerifyDone(true);
        }
      } finally {
        setVerifyLoading(false);
      }
    }, 350);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [verifyQ, tab]);

  React.useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/wall-of-fame")
      .then((r) => { setData(r.data || { professionals: [], companies: [], recruiters: [], professionals_count: 0, companies_count: 0, recruiters_count: 0 }); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [user]);

  // Fetch certificate photos on-demand — keeps list response light (~1 MB
  // instead of ~18 MB). Iteration 72 slim-mode optimization.
  const openPhotoFetch = React.useCallback(async (kind, recId) => {
    if (!kind || !recId) return;
    setPhotoLoading(true);
    try {
      const r = await api.get(`/wall-of-fame/photos/${kind}/${recId}`);
      const photos = r.data?.photos || [];
      if (photos.length > 0) setOpenPhoto(photos);
    } catch (e) {
      console.error("photo fetch failed", e);
    } finally {
      setPhotoLoading(false);
    }
  }, []);

  // Auth gate — Sieň slávy is for registered members only. Any tier (Free
  // included) works — registration is the barrier, not the paywall.
  if (!authLoading && !user) {
    return (
      <div className="min-h-screen bg-black text-zinc-200" data-testid="wall-of-fame-gate">
        <div className="max-w-2xl mx-auto px-6 py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-gold-400 mb-8">
            <ArrowLeft className="w-3 h-3" /> {t.back}
          </Link>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-gold-400 mb-3">
            <Trophy className="w-3 h-3" /> {t.eyebrow}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-white leading-[1.05] mb-5">
            {t.title}
          </h1>
          <div className="border border-gold-700/40 bg-gradient-to-br from-gold-950/40 to-zinc-950 p-7 mt-6">
            <Lock className="w-10 h-10 text-gold-400 mb-5" />
            <h2 className="font-display font-bold text-2xl text-white mb-3">{t.gateTitle}</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">{t.gateBody}</p>
            <p className="text-xs text-zinc-500 leading-relaxed mb-6">
              {t.gateOfficialLink}{" "}
              <a href="https://saubereko.eu" target="_blank" rel="noopener noreferrer"
                 className="text-gold-400 hover:text-gold-300 underline decoration-gold-800 underline-offset-4"
                 data-testid="wall-of-fame-gate-saubereko-link">
                saubereko.eu
              </a>
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-5 py-3 bg-gold-500 hover:bg-gold-400 text-black font-mono uppercase tracking-wider text-xs font-bold transition-colors"
                data-testid="wall-of-fame-gate-register"
              >
                <UserPlus className="w-3 h-3" /> {t.gateRegister}
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-5 py-3 border border-gold-700/40 text-gold-300 hover:bg-gold-900/20 font-mono uppercase tracking-wider text-xs"
                data-testid="wall-of-fame-gate-login"
              >
                <LogIn className="w-3 h-3" /> {t.gateLogin}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="wall-of-fame-page">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-gold-400 mb-8">
          <ArrowLeft className="w-3 h-3" /> {t.back}
        </Link>

        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-gold-400 mb-3">
          <Trophy className="w-3 h-3" /> {t.eyebrow}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-4">{t.title}</h1>
        <p className="text-base text-zinc-400 max-w-3xl">{t.subtitle}</p>
        <p className="text-sm text-zinc-500 mt-3" data-testid="wall-main-hof-link">
          {ct.main}{" "}
          <a href="https://saubereko.eu" target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:text-gold-300 underline underline-offset-4">saubereko.eu</a>
        </p>

        <CandidateSearchPanel lang={lang} />

        {/* Tabs */}
        <div className="mt-10 flex gap-1 sm:gap-2 border-b border-zinc-800 overflow-x-auto no-scrollbar">
          <button
            onClick={() => { setTab("pros"); setVerifyQ(""); setVerifyResults([]); setVerifyDone(false); }}
            data-testid="wall-tab-pros"
            className={`px-2 sm:px-5 py-3 text-[10px] sm:text-xs font-mono uppercase tracking-tight sm:tracking-widest inline-flex items-center gap-1 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
              tab === "pros" ? "border-gold-500 text-gold-400" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <User className="w-3 h-3" /> {t.tabPros} <span className="text-[10px] text-zinc-600">({data.professionals_count})</span>
          </button>
          <button
            onClick={() => { setTab("companies"); setVerifyQ(""); setVerifyResults([]); setVerifyDone(false); }}
            data-testid="wall-tab-companies"
            className={`px-2 sm:px-5 py-3 text-[10px] sm:text-xs font-mono uppercase tracking-tight sm:tracking-widest inline-flex items-center gap-1 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
              tab === "companies" ? "border-gold-500 text-gold-400" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Building2 className="w-3 h-3" /> {t.tabCompanies} <span className="text-[10px] text-zinc-600">({data.companies_count})</span>
          </button>
          <button
            onClick={() => { setTab("supporters"); setVerifyQ(""); setVerifyResults([]); setVerifyDone(false); }}
            data-testid="wall-tab-supporters"
            className={`px-2 sm:px-5 py-3 text-[10px] sm:text-xs font-mono uppercase tracking-tight sm:tracking-widest inline-flex items-center gap-1 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
              tab === "supporters" ? "border-gold-500 text-gold-400" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <HeartHandshake className="w-3 h-3" /> {(SUP_TR[lang] || SUP_TR.sk).tab} <span className="text-[10px] text-zinc-600">({supporters.length})</span>
          </button>
        </div>

        {/* Professionals tab — Iteration 74: search-only, no public listing */}
        {tab === "pros" && (
          <VerifySearchPanel
            t={t}
            query={verifyQ}
            onQueryChange={setVerifyQ}
            loading={verifyLoading}
            results={verifyResults}
            done={verifyDone}
            kind="professional"
            count={data.professionals_count}
            countLabel={ct.pros}
          />
        )}

        {tab === "companies" && (
          <VerifySearchPanel
            t={t}
            query={verifyQ}
            onQueryChange={setVerifyQ}
            loading={verifyLoading}
            results={verifyResults}
            done={verifyDone}
            kind="company"
            texts={ct}
            count={data.companies_count}
            countLabel={ct.comps}
            renderCard={(c) => <CompanyCard c={c} t={t} onOpenPhotoFetch={openPhotoFetch} />}
          />
        )}

        {tab === "supporters" && <SupportersPanel items={supporters} lang={lang} />}
      </div>

      <CertificateModal items={openPhoto} onClose={() => setOpenPhoto(null)} t={t} />
    </div>
  );
}
